import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Chip,
  Alert,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DoneIcon from '@mui/icons-material/Done';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FilterListIcon from '@mui/icons-material/FilterList';
import InfoIcon from '@mui/icons-material/Info';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SpeedIcon from '@mui/icons-material/Speed';
import ExploreIcon from '@mui/icons-material/Explore';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

type Operator = 'eq' | 'ne' | 'contains' | 'startswith' | 'endswith' | 'gt' | 'ge' | 'lt' | 'le';

interface FilterRow {
  field: string;
  operator: Operator;
  value: string;
}

interface ODataQueryBuilderProps {
  accessToken: string;
  onBack: () => void;
}

// Simple schema suggestions for common resources used in app config scenarios
const fieldSuggestions: Record<string, string[]> = {
  '/applications': [
    'id', 'appId', 'displayName', 'publisherDomain', 'signInAudience',
    'identifierUris', 'tags', 'requiredResourceAccess', 'passwordCredentials'
  ],
  '/servicePrincipals': [
    'id', 'appId', 'displayName', 'tags', 'appOwnerOrganizationId', 'servicePrincipalType'
  ],
  '/users': [
    'id', 'userPrincipalName', 'displayName', 'mail', 'accountEnabled'
  ],
  '/groups': [
    'id', 'displayName', 'mail', 'groupTypes', 'securityEnabled'
  ],
};

const resourceOptions = Object.keys(fieldSuggestions);

function encodeValueForFilter(op: Operator, value: string): string {
  // wrap strings in quotes; leave numeric-looking values as-is for comparison ops
  const numericOps: Operator[] = ['gt', 'ge', 'lt', 'le'];
  const isNumeric = /^-?\d+(\.\d+)?$/.test(value);
  if (numericOps.includes(op) && isNumeric) return value;
  // escape single quotes by doubling them per OData
  const escaped = value.replace(/'/g, "''");
  return `'${escaped}'`;
}

function buildFilterClause(filters: FilterRow[]): string | null {
  const parts = filters
    .filter(f => f.field && f.operator && f.value !== '')
    .map(f => {
      if (['contains', 'startswith', 'endswith'].includes(f.operator)) {
        return `${f.operator}(${f.field}, ${encodeValueForFilter(f.operator, f.value)})`;
      }
      return `${f.field} ${f.operator} ${encodeValueForFilter(f.operator, f.value)}`;
    });
  if (!parts.length) return null;
  // Join with and by default; could add UI toggle to switch to or in future
  return parts.join(' and ');
}

export const ODataQueryBuilder: React.FC<ODataQueryBuilderProps> = ({ accessToken, onBack }) => {
  const [resource, setResource] = useState<string>('/applications');
  const [selectFields, setSelectFields] = useState<string[]>(['displayName']);
  const [filters, setFilters] = useState<FilterRow[]>([{ field: '', operator: 'eq', value: '' }]);
  const [orderBy, setOrderBy] = useState<string>('');
  const [orderDir, setOrderDir] = useState<'asc' | 'desc'>('asc');
  // Make $top optional; empty means default page size
  const [top, setTop] = useState<string>('');
  const [includeCount, setIncludeCount] = useState<boolean>(false);
  const [fetchAll, setFetchAll] = useState<boolean>(false);
  const [apiVersion, setApiVersion] = useState<'v1.0' | 'beta'>('v1.0');
  const [response, setResponse] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [lastJson, setLastJson] = useState<any | null>(null);

  const availableFields = useMemo(() => fieldSuggestions[resource] || [], [resource]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (selectFields.length) params.set('$select', selectFields.join(','));
    const filter = buildFilterClause(filters);
    if (filter) params.set('$filter', filter);
  if (orderBy) params.set('$orderby', `${orderBy} ${orderDir}`);
  if (top && top.trim()) params.set('$top', top.trim());
    if (includeCount) params.set('$count', 'true');
    const qs = params.toString();
    return `${resource}${qs ? `?${qs}` : ''}`;
  }, [resource, selectFields, filters, orderBy, orderDir, top, includeCount]);

  const fullUrl = `https://graph.microsoft.com/${apiVersion}${queryString}`;

  const addFilterRow = () => setFilters(prev => [...prev, { field: '', operator: 'eq', value: '' }]);
  const removeFilterRow = (idx: number) => setFilters(prev => prev.filter((_, i) => i !== idx));
  const updateFilterRow = (idx: number, patch: Partial<FilterRow>) =>
    setFilters(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const handleRun = async () => {
    setResponse('');
    setError('');
    setLastJson(null);
    try {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };
      if (includeCount) headers['ConsistencyLevel'] = 'eventual';

      if (!fetchAll) {
        const res = await fetch(fullUrl, { headers });
        const statusLine = `Status: ${res.status} ${res.statusText}\n`;
        let text = await res.text();
        try {
          const data = JSON.parse(text);
          setLastJson(data);
          text = JSON.stringify(data, null, 2);
        } catch {}
        setResponse(`${statusLine}\n${text}`);
        if (!res.ok && (res.status === 401 || res.status === 403)) {
          setError('Authentication/authorization error. You may need additional scopes for this resource.');
        }
        return;
      }

      // Fetch all pages by following @odata.nextLink
      let url: string | null = fullUrl;
      let all: any[] = [];
      let page = 0;
      const MAX_PAGES = 200; // safety
      let firstStatus: string | null = null;

      while (url && page < MAX_PAGES) {
        page++;
        const res = await fetch(url, { headers });
        if (!firstStatus) firstStatus = `Status: ${res.status} ${res.statusText}`;
        const text = await res.text();
        let json: any = null;
        try { json = JSON.parse(text); } catch { /* not JSON */ }
        if (json && Array.isArray(json.value)) {
          all.push(...json.value);
          url = json['@odata.nextLink'] || null;
        } else {
          // Not a collection response; show as-is
          const pretty = json ? JSON.stringify(json, null, 2) : text;
          setLastJson(json ?? null);
          setResponse(`${firstStatus || ''}\n\n${pretty}`);
          if (!res.ok && (res.status === 401 || res.status === 403)) {
            setError('Authentication/authorization error. You may need additional scopes for this resource.');
          }
          return;
        }
      }

      const aggregated = {
        info: {
          pages: page,
          items: all.length,
          truncated: page >= MAX_PAGES,
        },
        value: all,
      };
      const finalText = JSON.stringify(aggregated, null, 2);
      setLastJson(aggregated);
      setResponse(`${firstStatus || ''}\n\n${finalText}`);
    } catch (e: any) {
      setError(e?.message || 'Request failed');
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 1500);
  };

  const handleClearResponse = () => {
    setResponse('');
    setError('');
    setLastJson(null);
  };

  const handleResetForm = () => {
    setResource('/applications');
    setSelectFields(['displayName']);
    setFilters([{ field: '', operator: 'eq', value: '' }]);
    setOrderBy('');
    setOrderDir('asc');
    setTop('');
    setIncludeCount(false);
    setFetchAll(false);
    setApiVersion('v1.0');
    handleClearResponse();
  };

  const handleCopyResponse = () => {
    if (response) {
      navigator.clipboard.writeText(response);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1500);
    }
  };

  const handleDownloadJson = () => {
    if (!lastJson) return;
    const blob = new Blob([JSON.stringify(lastJson, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const resName = resource.replace(/^\//, '').replace(/\//g, '_') || 'result';
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `${resName}-${apiVersion}-${ts}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f4f8fb', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ mr: 2, borderRadius: 2, pl: 1.5, color: 'text.secondary', borderColor: 'divider', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', borderColor: 'primary.main', color: 'primary.main' } }}
          >
            Back
          </Button>
          <Box sx={{ borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, py: 0.5 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main', letterSpacing: '-0.01em' }}>
              OData Query Builder
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Build OData queries visually and run them against Microsoft Graph
            </Typography>
          </Box>
        </Box>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, mb: 2 }}>
                <FilterAltIcon sx={{ mr: 1, color: 'primary.main' }} /> Query Parameters
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="resource-label">Resource</InputLabel>
                    <Select
                      labelId="resource-label"
                      value={resource}
                      label="Resource"
                      onChange={(e: SelectChangeEvent) => {
                        const val = e.target.value as string;
                        setResource(val);
                        // reset select fields to reasonable defaults (excluding id)
                        const defaultFields = fieldSuggestions[val]?.slice(0, 3).filter(f => f !== 'id') || [];
                        setSelectFields(defaultFields.length ? defaultFields : ['displayName']);
                      }}
                    >
                      {resourceOptions.map(r => (
                        <MenuItem key={r} value={r}>{r}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="$top (page size, optional)"
                    type="number"
                    value={top}
                    onChange={(e) => setTop(e.target.value)}
                    placeholder="Leave empty for default"
                    fullWidth
                    helperText="Controls page size only. Enable 'Fetch all pages' to aggregate all results."
                  />
                </Grid>

                {/* $filter rows - MOVED FIRST */}
                <Grid item xs={12}>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 2, 
                      backgroundColor: '#fef3c7',
                      border: '2px solid #fbbf24'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          color: '#b45309', 
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <FilterListIcon sx={{ mr: 1, color: '#b45309' }} />
                        $filter - Set Conditions
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                          variant="contained" 
                          size="small" 
                          onClick={addFilterRow} 
                          sx={{ 
                            borderRadius: 2,
                            backgroundColor: '#f59e0b',
                            '&:hover': { backgroundColor: '#d97706' }
                          }}
                        >
                          + Add condition
                        </Button>
                        {filters.length > 1 && (
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => setFilters([{ field: '', operator: 'eq', value: '' }])}
                            sx={{ borderRadius: 2, color: '#dc2626', borderColor: '#dc2626' }}
                            startIcon={<ClearIcon />}
                          >
                            Clear all
                          </Button>
                        )}
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, color: '#92400e', fontStyle: 'italic' }}>
                      Filter results based on field values and conditions. Define what you want to find first.
                    </Typography>
                    {filters.map((row, idx) => (
                      <Grid container spacing={1} key={idx} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={5}>
                          <TextField
                            label="Field"
                            placeholder="displayName"
                            value={row.field}
                            onChange={(e) => updateFilterRow(idx, { field: e.target.value })}
                            fullWidth
                            inputProps={{ list: `field-suggestions-${idx}` }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                '&:hover': {
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#f59e0b'
                                  }
                                }
                              }
                            }}
                          />
                          <datalist id={`field-suggestions-${idx}`}>
                            {availableFields.map(f => (
                              <option key={f} value={f} />
                            ))}
                          </datalist>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <FormControl fullWidth>
                            <InputLabel id={`op-${idx}`}>Operator</InputLabel>
                            <Select
                              labelId={`op-${idx}`}
                              label="Operator"
                              value={row.operator}
                              onChange={(e) => updateFilterRow(idx, { operator: e.target.value as Operator })}
                              sx={{
                                backgroundColor: 'white',
                                '&:hover': {
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#f59e0b'
                                  }
                                }
                              }}
                            >
                              {(['eq','ne','contains','startswith','endswith','gt','ge','lt','le'] as Operator[]).map(op => (
                                <MenuItem key={op} value={op}>{op}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <TextField
                            label="Value"
                            value={row.value}
                            onChange={(e) => updateFilterRow(idx, { value: e.target.value })}
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                '&:hover': {
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#f59e0b'
                                  }
                                }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={1}>
                          <Button 
                            variant="text" 
                            color="error" 
                            onClick={() => removeFilterRow(idx)} 
                            disabled={filters.length === 1}
                            sx={{ minWidth: 'auto', px: 1 }}
                          >
                            Remove
                          </Button>
                        </Grid>
                      </Grid>
                    ))}
                  </Paper>
                </Grid>

                {/* $select - MOVED SECOND */}
                <Grid item xs={12}>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 2, 
                      backgroundColor: '#f0f9ff',
                      border: '2px solid #bae6fd'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          color: '#0369a1', 
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <CheckBoxIcon sx={{ mr: 1, color: '#0369a1' }} />
                        $select - Choose Fields
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setSelectFields(availableFields)}
                          sx={{ 
                            borderRadius: 2,
                            color: '#0369a1',
                            borderColor: '#0369a1',
                            '&:hover': {
                              backgroundColor: '#e0f2fe',
                              borderColor: '#0284c7'
                            }
                          }}
                        >
                          Select All
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setSelectFields([])}
                          sx={{ 
                            borderRadius: 2,
                            color: '#0369a1',
                            borderColor: '#0369a1',
                            '&:hover': {
                              backgroundColor: '#e0f2fe',
                              borderColor: '#0284c7'
                            }
                          }}
                        >
                          Select None
                        </Button>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, color: '#0c4a6e', fontStyle: 'italic' }}>
                      Select which properties to include in the response. Choose based on your filtering results above.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {availableFields.map(f => {
                        const selected = selectFields.includes(f);
                        return (
                          <Chip
                            key={f}
                            label={f}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => {
                              setSelectFields(prev => selected ? prev.filter(x => x !== f) : [...prev, f]);
                            }}
                            sx={{ 
                              borderRadius: 1.5,
                              fontWeight: selected ? 600 : 400,
                              '&:hover': {
                                transform: 'scale(1.05)',
                                transition: 'transform 0.1s ease-in-out'
                              }
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Paper>
                </Grid>

                {/* $orderby */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="$orderby field"
                    placeholder="displayName"
                    value={orderBy}
                    onChange={(e) => setOrderBy(e.target.value)}
                    fullWidth
                    inputProps={{ list: 'orderby-field-suggestions' }}
                  />
                  <datalist id="orderby-field-suggestions">
                    {availableFields.map(f => (
                      <option key={f} value={f} />
                    ))}
                  </datalist>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="dir-label">Direction</InputLabel>
                    <Select
                      labelId="dir-label"
                      value={orderDir}
                      label="Direction"
                      onChange={(e) => setOrderDir(e.target.value as 'asc' | 'desc')}
                    >
                      <MenuItem value="asc">asc</MenuItem>
                      <MenuItem value="desc">desc</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Preview */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                    Preview
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: '#f9fafb' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {fullUrl}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={copySuccess ? <DoneIcon /> : <ContentCopyIcon />}
                        onClick={() => handleCopy(fullUrl)}
                        sx={{ borderRadius: 2 }}
                      >
                        {copySuccess ? 'Copied!' : 'Copy URL'}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {includeCount && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  Using $count requires header ConsistencyLevel: eventual (added automatically)
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button 
                  variant="contained" 
                  onClick={handleRun} 
                  sx={{ borderRadius: 2 }}
                  startIcon={<PlayArrowIcon />}
                  size="large"
                >
                  Run Query
                </Button>
                <Button
                  variant={includeCount ? 'contained' : 'outlined'}
                  color={includeCount ? 'secondary' : 'inherit'}
                  onClick={() => setIncludeCount(v => !v)}
                  sx={{ borderRadius: 2 }}
                >
                  {includeCount ? 'Disable $count' : 'Enable $count'}
                </Button>
                <Button
                  variant={fetchAll ? 'contained' : 'outlined'}
                  color={fetchAll ? 'secondary' : 'inherit'}
                  onClick={() => setFetchAll(v => !v)}
                  sx={{ borderRadius: 2 }}
                >
                  {fetchAll ? 'Fetch all pages: ON' : 'Fetch all pages: OFF'}
                </Button>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="api-version-label">API Version</InputLabel>
                  <Select
                    labelId="api-version-label"
                    value={apiVersion}
                    label="API Version"
                    onChange={(e) => setApiVersion(e.target.value as 'v1.0' | 'beta')}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="v1.0">v1.0</MenuItem>
                    <MenuItem value="beta">beta</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  onClick={handleDownloadJson}
                  disabled={!lastJson}
                  sx={{ borderRadius: 2 }}
                  startIcon={<DownloadIcon />}
                >
                  Download JSON
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleResetForm}
                  sx={{ borderRadius: 2, color: 'text.secondary' }}
                  startIcon={<RefreshIcon />}
                >
                  Reset All
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Response */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  <DataObjectIcon sx={{ mr: 1, color: 'primary.main' }} />
                  Response
                </Typography>
                {(response || error) && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {response && (
                      <Tooltip title="Copy response to clipboard">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleCopyResponse}
                          sx={{ borderRadius: 2 }}
                          startIcon={copySuccess ? <DoneIcon /> : <ContentCopyIcon />}
                        >
                          {copySuccess ? 'Copied!' : 'Copy'}
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip title="Clear response">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClearResponse}
                        sx={{ borderRadius: 2, color: 'warning.main', borderColor: 'warning.main' }}
                        startIcon={<ClearIcon />}
                      >
                        Clear
                      </Button>
                    </Tooltip>
                  </Box>
                )}
              </Box>
              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
              )}
              {!response ? (
                <Paper
                  variant="outlined"
                  sx={{ 
                    p: 4, 
                    borderRadius: 2, 
                    textAlign: 'center', 
                    color: 'text.secondary', 
                    backgroundColor: '#f9f9f9',
                    border: '2px dashed #e0e0e0'
                  }}
                >
                  <DataObjectIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                    No response yet
                  </Typography>
                  <Typography variant="body2">
                    Build your query and click "Run Query" to see results here
                  </Typography>
                </Paper>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{ 
                    p: 3, 
                    borderRadius: 2, 
                    backgroundColor: '#f4f4f4', 
                    maxHeight: 500, 
                    overflowY: 'auto',
                    position: 'relative'
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.4,
                      fontSize: '0.875rem'
                    }}
                  >
                    {response}
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Enhanced About Section */}
      <Accordion 
        variant="outlined" 
        sx={{ 
          borderRadius: 2, 
          bgcolor: '#f9f9f9', 
          mt: 3,
          '&:before': { display: 'none' }, // Remove default MUI accordion divider
          border: '1px solid #e2e8f0'
        }}
        disableGutters
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            '&.Mui-expanded': {
              borderBottom: '2px solid',
              borderColor: 'info.main'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>About OData Query Builder</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Main Description */}
            <Grid item xs={12} md={8}>
              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6, color: 'text.secondary' }}>
                Build sophisticated Microsoft Graph queries using a visual interface. Select specific fields, 
                apply filters, sort results, and handle pagination—all without writing complex OData syntax manually.
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                  Key Features:
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                  <li><Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>Visual field selection with $select parameter</Typography></li>
                  <li><Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>Interactive filtering with multiple conditions</Typography></li>
                  <li><Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>Automatic pagination and result aggregation</Typography></li>
                  <li><Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>Support for both v1.0 and beta Graph APIs</Typography></li>
                  <li><Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>JSON export and response management</Typography></li>
                </Box>
              </Box>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                Best For:
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                Exploring directory objects (applications, service principals, users, groups), 
                bulk data queries, filtering large datasets, and quick API testing scenarios.
              </Typography>
            </Grid>
            
            {/* Comparison with Graph Explorer */}
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(227, 242, 253, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', color: '#1976d2' }}>
                  <CompareArrowsIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                  vs Graph Explorer
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#2e7d32', display: 'flex', alignItems: 'center' }}>
                    <SpeedIcon sx={{ fontSize: '0.9rem', mr: 0.5 }} />
                    Use OData Builder for:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
                    • Quick list queries
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
                    • Visual field selection
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
                    • Bulk data export
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    • Filtering collections
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#ed6c02', display: 'flex', alignItems: 'center' }}>
                    <ExploreIcon sx={{ fontSize: '0.9rem', mr: 0.5 }} />
                    Use Graph Explorer for:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
                    • Custom headers/bodies
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
                    • Write operations (POST/PATCH)
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
                    • Single resource endpoints
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    • Scope consent management
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          
          {/* Footer with tips */}
          <Box sx={{ 
            mt: 3,
            pt: 2,
            borderTop: '1px solid #e0e0e0'
          }}>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontStyle: 'italic',
              textAlign: 'center'
            }}>
              💡 Tip: Use "Select None" to retrieve all available properties, or combine multiple filters with AND logic for precise results
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Container>
  );
};

export default ODataQueryBuilder;
